/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { useClientEnvVarsStore, useDCAPageModelStore } from '@marriott/mi-store-utils';
import { getDataFromPath, setDataFromPath, transformTargetModel } from '../lib/dynamicContentAssemblyUtils';
import { targetPayloadRequest } from '../lib/dynamicContentAssemblyUtils/targetSDKPayload';
import { targetPayloadRequestV2 } from '../lib/dynamicContentAssemblyUtils/targetSDKPayloadV2';
import { dcaDynamicContent } from '@marriott/mi-merch-graphql';
import { dcaDynamicContentV2 } from '@marriott/mi-merch-graphql';
import { useNextMiApolloClient } from '@marriott/mi-apollo-client-utils';
import createMappingModelJSON from '../lib/dynamicContentAssemblyUtils/responseMapping';
import operationSignatures from '../lib/dynamicContentAssemblyUtils/_constants/operationSignatures.json';
import { AuthoringUtils } from '@adobe/aem-spa-page-model-manager';
import isEmpty from 'lodash/isEmpty';
import { IDCAResponse, IPageModel, IReturnTypeOFGetMboxParam } from '../types';
import { logger } from '@marriott/shared/mi-helper-utils';
import { MvpOffersData, useSession } from '../lib/session';
import { getAepCookieName, getLocale, isV2UxlEnabled } from '../lib/helpers';
import { focConfirmationCall } from './analyticsUtils';
import { useBrandTrigger } from './useBrandTrigger';
import { targetBrandBannerPayload } from '../lib/dynamicContentAssemblyUtils/targetBrandBannerPayload';
import { decodeKndctrCookie } from '../lib/dynamicContentAssemblyUtils/decodeKndctrCookie';
import { getIdentityData, getClusterData } from './use-identity';
import { useAlloy } from './use-alloy';
import Cookies from 'js-cookie';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const transformJSON = require('json-map-transform');

// @todo - need to fill this out and have clients of this pass in a type for T
export type DCAModelData = {
  isTargetContentLoaded?: boolean;
  isTargetContentError?: boolean;
  metaData: Record<string, string>;
};

export const useDCADynamicModel = (
  mboxParameters: IReturnTypeOFGetMboxParam[],
  pageModel: IPageModel,
  enableBannerTriggerLogic: boolean
) => {
  if (mboxParameters?.length == 0) {
    return null;
  }

  const { log } = logger({})('useDCADynamicModel');
  const [isPrequal, setIsPrequal] = useState<boolean>(false);
  const { datalayer, mvpOffers } = useSession();
  const setPageModel = useDCAPageModelStore(state => state.setPageModel);
  const [isUXlCallTriggered, setIsUXLCallTriggered] = useState(false);
  const { alloy } = useAlloy();
  const brandsData = useBrandTrigger(enableBannerTriggerLogic);

  /**
   *
   * @param listOfMboxResponses refers to mboxes array of uxl call response
   * @description this function will store the updated pageModel in DCAPageModelStore
   * case-1: when uxl call is successfull pass that response to this function, then it will update pageModel according to that response..
   * case-2: if call fails, then simply call this function
   * then it will update pageModel with isTargetContentError flag for the appropriate mbox components
   *
   */
  const updatePageModel = (
    listOfMboxResponses: Record<string, unknown>[] = [],
    brandParams: Record<string, string> = {}
  ) => {
    const newPageModel = { ...pageModel };
    const { variation, mboxToUpdate } = isOfferVriationEnabled(mboxParameters, listOfMboxResponses);
    mboxParameters?.forEach(parameter => {
      const mboxName = parameter?.['mboxName'];
      const mboxResponse = listOfMboxResponses?.find(item => item?.['mboxName'] == mboxName);
      const requiredPath = parameter?.path; // returns path of component eg: cqItems.merchHeroBanner
      let compModel = getDataFromPath(pageModel, requiredPath); // returns component data from pageModel
      if (variation && mboxName === mboxToUpdate && compModel?.['variation'] !== variation) {
        compModel = updateHeroVariationForABTest(variation, compModel);
      }
      if (mboxResponse) {
        const mappingModelJSON = createMappingModelJSON();
        const mappedResponse = transformJSON(mboxResponse, mappingModelJSON);
        const transformerData = transformTargetModel(compModel, mappedResponse);
        parameter.dcaResponse = {
          ...transformerData,
          isTargetContentLoaded: true,
          isTargetContentError: false,
          brandParams: brandParams,
        };
        parameter.uxlResponse = { ...mappedResponse, isTargetContentLoaded: true, isTargetContentError: false }; // this key will be used for container components
        setDataFromPath(newPageModel, requiredPath, { ...transformerData, isTargetContentLoaded: true });
      } else {
        parameter.dcaResponse = {
          ...compModel,
          isTargetContentLoaded: true,
          isTargetContentError: true,
        };
        parameter.uxlResponse = { isTargetContentLoaded: true, isTargetContentError: true }; // this key will be used for container components
        setDataFromPath(newPageModel, requiredPath, {
          ...compModel,
          isTargetContentLoaded: true,
          isTargetContentError: true,
        });
      }
    });
    setPageModel({
      ...newPageModel,
      mboxData: [...mboxParameters],
    });
  };

  const getDCAPayload = (isV2Uxl: boolean, mvpOffers?: MvpOffersData, brandPayload?: Record<string, string>[]) => {
    const targetPayloadMethod = isV2Uxl ? targetPayloadRequestV2 : targetPayloadRequest;
    return targetPayloadMethod(
      {
        locale: getLocale(),
        mboxParameters: mboxParameters,
        propertyToken:
          process.env['TARGET_PROPERTY_TOKEN'] ||
          useClientEnvVarsStore.getState().envVarsObject['TARGET_PROPERTY_TOKEN'],
        isACDL: useClientEnvVarsStore.getState().envVarsObject?.['NEXT_PUBLIC_ENABLE_WEBSDK'],
      },
      datalayer,
      mvpOffers,
      brandPayload
    );
  };

  useEffect(() => {
    if (isEmpty(datalayer)) {
      log.info('datalayer is empty');
      return;
    }
    if (!AuthoringUtils.isInEditor() && AuthoringUtils.isPreviewMode()) {
      log.info('AuthoringUtils.isInEditor() && AuthoringUtils.isPreviewMode()');
      return;
    }
    if (enableBannerTriggerLogic && !brandsData) {
      log.info('brands data fetch not complete');
      return;
    }
    const fetchAndUpdateTargetData = async () => {
      setIsUXLCallTriggered(true);
      const isV2Uxl = isV2UxlEnabled();
      const { brandPayload, brandParams } = targetBrandBannerPayload(brandsData, datalayer);
      const options = getDCAPayload(isV2Uxl, mvpOffers, brandPayload);
      if (!AuthoringUtils.isInEditor() && !AuthoringUtils.isPreviewMode()) {
        try {
          if (isV2Uxl) {
            // if identity cookies are not present, restrict DCA call
            let identityCookie = decodeKndctrCookie();
            const clusterCookieName = getAepCookieName('cluster');
            let clusterCookie = Cookies.get(clusterCookieName);

            if (!identityCookie && alloy) {
              await getIdentityData(alloy);
              identityCookie = decodeKndctrCookie();
              if (!identityCookie) {
                throw new Error('identity cookies not present');
              }
            }

            if (!clusterCookie && alloy) {
              await getClusterData(alloy);
              clusterCookie = Cookies.get(clusterCookieName);
              if (!clusterCookie) {
                throw new Error('cluster cookie not set');
              }
            }

            //if property Token is missing, restrict DCA v2 call
            if (!options?.context?.overrides?.target?.[0]?.value) {
              throw new Error('property token not present');
            }
          } else {
            //if property Token is missing, restrict DCA v1 call
            if (!options?.property?.token) {
              throw new Error('property token not present');
            }
          }
          const MiApolloClient = useNextMiApolloClient(
            operationSignatures,
            useClientEnvVarsStore.getState().envVarsObject['DEPLOYED_ENV_TYPE']
          );
          const variables = isV2Uxl
            ? {
                input: options,
                maxRequested: 1,
                slotsAvailable: 1,
              }
            : {
                input: options,
              };

          const { data, errors } = await MiApolloClient.query({
            query: isV2Uxl ? dcaDynamicContentV2 : dcaDynamicContent,
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
            variables: variables,
            context: {
              headers: {},
            },
          });
          const respObj = isV2Uxl ? 'dynamicContentByMboxes' : 'dynamicContentByChannelAndMboxes';
          const resp = data?.[respObj]?.mboxes as IDCAResponse[];
          if (errors && errors.length && resp?.length == 0) {
            throw new Error('DCA call error or mbox parameter is empty');
          }

          if (resp && resp?.length > 0) {
            resp?.forEach(item => {
              if (
                String((item?.['metadata'] as Record<string, unknown>)?.['prequal']) === 'true' &&
                String(mvpOffers?.isFocComplete) === 'false'
              ) {
                setIsPrequal(true);
              }
            });
          }
          const updatedResp = resp.filter(item => String(item?.['metadata']?.['defaultContent']) !== 'true');
          updatePageModel(updatedResp, brandParams);
        } catch (error) {
          console.log(error);
          updatePageModel();
        }
      }
    };
    if (!isEmpty(datalayer) && !isUXlCallTriggered) {
      fetchAndUpdateTargetData();
    }
  }, [datalayer, mvpOffers, isUXlCallTriggered, brandsData]);

  useEffect(() => {
    if (isPrequal) {
      focConfirmationCall('show', datalayer, mvpOffers);
    }
  }, [isPrequal]);

  return null;
};

export const fetchDCADynamicModelForAlloy = (
  mboxParameters: IReturnTypeOFGetMboxParam[],
  pageModel: IPageModel,
  datalayer: Record<string, unknown>,
  mvpOffers: MvpOffersData | undefined
): Promise<Record<string, unknown> | Record<string, unknown>[]> => {
  return new Promise(resolve => {
    if (mboxParameters?.length == 0) {
      resolve({});
    } else if (!isEmpty(datalayer) && !AuthoringUtils.isInEditor() && !AuthoringUtils.isPreviewMode()) {
      const isV2Uxl = isV2UxlEnabled();
      const options = getDCAPayload(isV2Uxl, datalayer, mboxParameters, mvpOffers);
      fetchTargetData(options, isV2Uxl)?.then(response => {
        if (!pageModel || Object.keys(pageModel)?.length == 0) {
          resolve(response as unknown as Record<string, unknown>[]);
        } else updatePageModel(mboxParameters, pageModel, response, resolve);
      });
    } else resolve({});
  });
};

const getDCAPayload = (
  isV2Uxl: boolean,
  datalayer: Record<string, unknown>,
  mboxParameters: IReturnTypeOFGetMboxParam[],
  mvpOffers?: MvpOffersData
) => {
  const targetPayloadMethod = isV2Uxl ? targetPayloadRequestV2 : targetPayloadRequest;
  return targetPayloadMethod(
    {
      locale: getLocale(),
      mboxParameters: mboxParameters,
      propertyToken:
        process.env['TARGET_PROPERTY_TOKEN'] || useClientEnvVarsStore.getState().envVarsObject['TARGET_PROPERTY_TOKEN'],
      isACDL: useClientEnvVarsStore.getState().envVarsObject?.['NEXT_PUBLIC_ENABLE_WEBSDK'],
    },
    datalayer,
    mvpOffers
  );
};

/**
 *
 * @param listOfMboxResponses refers to mboxes array of uxl call response
 * @description this function will store the updated pageModel in DCAPageModelStore
 * case-1: when uxl call is successfull pass that response to this function, then it will update pageModel according to that response..
 * case-2: if call fails, then simply call this function
 * then it will update pageModel with isTargetContentError flag for the appropriate mbox components
 *
 */
const updatePageModel = (
  mboxParameters: IReturnTypeOFGetMboxParam[],
  pageModel: IPageModel,
  listOfMboxResponses: Record<string, unknown>[] = [],
  callback: CallableFunction
) => {
  const newPageModel = { ...pageModel };
  mboxParameters?.forEach(parameter => {
    const mboxName = parameter?.['mboxName'];
    const mboxResponse = listOfMboxResponses?.find(item => item?.['mboxName'] == mboxName);
    const requiredPath = parameter?.path; // returns path of component eg: cqItems.merchHeroBanner
    const compModel = getDataFromPath(pageModel, requiredPath); // returns component data from pageModel
    if (mboxResponse) {
      const mappingModelJSON = createMappingModelJSON();
      const mappedResponse = transformJSON(mboxResponse, mappingModelJSON);
      const transformerData = transformTargetModel(compModel, mappedResponse);
      parameter.dcaResponse = { ...transformerData, isTargetContentLoaded: true, isTargetContentError: false };
      setDataFromPath(newPageModel, requiredPath, { ...transformerData, isTargetContentLoaded: true });
    } else {
      parameter.dcaResponse = {
        ...compModel,
        isTargetContentLoaded: true,
        isTargetContentError: true,
      };
      setDataFromPath(newPageModel, requiredPath, {
        ...compModel,
        isTargetContentLoaded: true,
        isTargetContentError: true,
      });
    }
  });
  callback &&
    callback({
      ...newPageModel,
      mboxData: [...mboxParameters],
    });
};

const fetchTargetData = async (options: unknown, isV2Uxl: boolean) => {
  try {
    const MiApolloClient = useNextMiApolloClient(
      operationSignatures,
      useClientEnvVarsStore.getState().envVarsObject['DEPLOYED_ENV_TYPE']
    );
    const { data, errors } = await MiApolloClient.query({
      query: isV2Uxl ? dcaDynamicContentV2 : dcaDynamicContent,
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
      variables: {
        input: options,
      },
      context: {
        headers: {},
      },
    });
    const respObj = isV2Uxl ? 'dynamicContentByMboxes' : 'dynamicContentByChannelAndMboxes';
    const resp = data?.[respObj]?.mboxes as Record<string, Record<string, unknown> | string>[];
    if (errors && errors.length && resp?.length == 0) {
      throw new Error('DCA call error or mbox parameter is empty');
    }
    return resp;
  } catch (error) {
    console.log(error);
    return [];
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const updateHeroVariationForABTest = (variation: string, modelJson: Record<string, any>) => {
  enum DAC_RENDITIONS {
    'WIDE_HORIZONTAL' = 'wideHorizontal',
    'CLASSIC_VERTICAL' = 'classicVertical',
    'PANORAMIC_HORIZONTAL' = 'panoramicHorizontal',
    'SQUARE' = 'square',
  }
  switch (variation) {
    case 'home_hero_3:1':
      modelJson?.['image']?.['renditions']?.forEach((rendition: Record<string, unknown>) => {
        if (rendition?.['mediaValue'] === '992px') {
          rendition['smartCropType'] = DAC_RENDITIONS.PANORAMIC_HORIZONTAL;
        } else if (rendition?.['mediaValue'] === '768px') {
          rendition['smartCropType'] = DAC_RENDITIONS.SQUARE;
        } else {
          rendition['smartCropType'] = DAC_RENDITIONS.CLASSIC_VERTICAL;
        }
        rendition['dynamic'] = false;
      });
      modelJson['heroSize'] = 'home_hero_3:1';
      break;
    case 'hero':
      modelJson?.['image']?.['renditions']?.forEach((rendition: Record<string, unknown>) => {
        if (rendition?.['mediaValue'] === '992px' || rendition?.['mediaValue'] === '768px') {
          rendition['smartCropType'] = DAC_RENDITIONS.WIDE_HORIZONTAL;
        } else {
          rendition['smartCropType'] = DAC_RENDITIONS.CLASSIC_VERTICAL;
        }
      });
      break;
    default:
      break;
  }
  return modelJson;
};

const isOfferVriationEnabled = (
  mboxParameters: IReturnTypeOFGetMboxParam[],
  listOfMboxResponses: Record<string, unknown>[] = []
) => {
  const wrapper = mboxParameters?.find(item => String(item?.enableOfferVariation) === 'true');
  if (wrapper) {
    const mboxResponse = listOfMboxResponses?.find(item => item?.['mboxName'] == wrapper?.mboxName);
    const variation = (mboxResponse?.['metadata'] as Record<string, string>)?.['variation'];
    const heroMboxParameter = mboxParameters?.find(
      item => item?.path !== wrapper?.path && item?.path?.includes(wrapper?.compKey)
    )?.mboxName;
    return { variation, mboxToUpdate: heroMboxParameter };
  } else {
    return {};
  }
};
